/**
 * Worker agents overlay — shows owned producers as cute animated icons
 * on the main tap screen, making them look like they're working
 */

import type { GameState } from '../game/index.js';

type GetState = () => GameState;

const PRODUCER_IMAGES: Record<string, string> = {
  informant: 'images/prod-informant.png',
  hacker: 'images/prod-hacker.png',
  disguiser: 'images/prod-disguiser.png',
  mindController: 'images/prod-mindController.png',
  secretLab: 'images/prod-secretLab.png',
  mediaCell: 'images/prod-mediaCell.png',
  shadowGovt: 'images/prod-shadowGovt.png',
  deepBase: 'images/prod-deepBase.png',
  orbitalStation: 'images/prod-orbitalStation.png',
  dimensionGate: 'images/prod-dimensionGate.png',
  planetController: 'images/prod-planetController.png',
  galacticBeacon: 'images/prod-galacticBeacon.png',
};

// Pre-defined positions (% based) around the edges, avoiding center
const WORKER_SLOTS = [
  { x: 12, y: 75 },  // bottom-left
  { x: 82, y: 72 },  // bottom-right
  { x: 8, y: 40 },   // mid-left
  { x: 86, y: 38 },  // mid-right
  { x: 18, y: 15 },  // top-left
  { x: 78, y: 12 },  // top-right
  { x: 35, y: 80 },  // bottom-center-left
  { x: 65, y: 82 },  // bottom-center-right
  { x: 5, y: 58 },   // left
  { x: 90, y: 55 },  // right
  { x: 50, y: 8 },   // top-center
  { x: 50, y: 88 },  // bottom-center
];

let containerEl: HTMLDivElement | null = null;
let getStateRef: GetState | null = null;
let lastProducerKey = '';

export function initWorkerAgents(getState: GetState): void {
  getStateRef = getState;
}

/** Call from mainScreen render to update worker display */
export function updateWorkerAgents(): void {
  if (!getStateRef) return;
  const state = getStateRef();

  const tapArea = document.getElementById('tap-area');
  if (!tapArea) return;

  // Build a key from owned producer IDs+counts to avoid re-rendering
  const ownedList = Object.entries(state.producers)
    .filter(([, p]) => p.count > 0)
    .map(([id, p]) => `${id}:${p.count}`);
  const key = ownedList.join('|');
  if (key === lastProducerKey && containerEl?.parentElement === tapArea) return;
  lastProducerKey = key;

  // Remove old container
  if (containerEl) containerEl.remove();
  if (ownedList.length === 0) { containerEl = null; return; }

  containerEl = document.createElement('div');
  containerEl.className = 'worker-agents-layer';
  // Insert before the character so workers appear behind
  tapArea.insertBefore(containerEl, tapArea.firstChild);

  const owned = Object.entries(state.producers)
    .filter(([, p]) => p.count > 0);

  owned.forEach(([id, prod], i) => {
    const imgSrc = PRODUCER_IMAGES[id];
    if (!imgSrc || i >= WORKER_SLOTS.length) return;

    const slot = WORKER_SLOTS[i];
    const el = document.createElement('div');
    el.className = 'worker-agent';
    // Vary animation: alternate between bob and sway
    const animType = i % 3 === 0 ? 'worker-walk' : i % 3 === 1 ? 'worker-bob' : 'worker-sway';
    const delay = (i * 0.7) % 3;
    const duration = 2 + (i % 3) * 0.5;
    el.style.cssText = `
      left:${slot.x}%;top:${slot.y}%;
      animation:${animType} ${duration}s ease-in-out ${delay}s infinite;
    `;

    const img = document.createElement('img');
    img.src = imgSrc;
    img.alt = id;
    img.draggable = false;
    el.appendChild(img);

    if (prod.count > 1) {
      const badge = document.createElement('span');
      badge.className = 'worker-count';
      badge.textContent = `x${prod.count}`;
      el.appendChild(badge);
    }

    containerEl!.appendChild(el);
  });
}
